/**
 * Artifact detail page (/artifact?id=…).
 *
 * Data comes from POST /artifacts/detail via the artifactDetail slice, mapped to
 * the ArtifactDetailView view model. Renders the artifact summary (headline
 * fields + the artifactDetails document), then the payments carried by the
 * artifact — found via SearchPayments (paymentIdentifiers=[artifactId]) and
 * shown as analytics + a table. The landing-page search bar is kept at the top
 * (without the "Beacon Lookup" title) so another lookup can be run without
 * navigating back.
 */
import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Spinner from '@cloudscape-design/components/spinner';
import Link from '@cloudscape-design/components/link';
import { useAppSelector, useAppDispatch } from 'src/store/hooks';
import {
  fetchArtifactDetail, clearArtifactDetail,
} from 'src/store/slices/artifactDetail/artifactDetailSlice';
import { searchPayments, clearSearchPayments } from 'src/store/slices/searchPayments/searchPaymentsSlice';
import { SearchTabs } from '../search/SearchTabs';
import { ArtifactSummary } from './summary/ArtifactSummary';
import { PaymentAnalytics } from '../payments/PaymentAnalytics';
import { PaymentDetailsTable } from '../payments/PaymentDetailsTable';

export const ArtifactDetailPage = () => {
  const [searchParams] = useSearchParams();
  const artifactId = searchParams.get('id') ?? '';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: detail, otherArtifactIds, loading, error, notFound } = useAppSelector(state => state.artifactDetail);

  const load = () => {
    if (!artifactId) return undefined;
    return dispatch(fetchArtifactDetail(artifactId));
  };

  // Load whenever the id changes; abort any in-flight request.
  useEffect(() => {
    const promise = load();
    const paymentsPromise = artifactId
      ? dispatch(searchPayments({ filters: { paymentIdentifiers: [artifactId] }, pagination: { pageNumber: 1, pageSize: 100 } }))
      : undefined;
    return () => {
      promise?.abort();
      paymentsPromise?.abort();
      dispatch(clearArtifactDetail());
      dispatch(clearSearchPayments());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifactId, dispatch]);

  const renderBody = () => {
    if (!artifactId) {
      return (
        <Alert type="warning" header="No artifact id">
          Provide an artifact id, for example /artifact?id=&lt;artifactId&gt;.
        </Alert>
      );
    }
    if (loading) {
      return (
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
          <Box variant="p" color="text-body-secondary">Loading artifact {artifactId}…</Box>
        </Box>
      );
    }
    if (error) {
      return (
        <Alert type="error" header="Failed to load artifact" action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      );
    }
    if (notFound || !detail) {
      return (
        <Alert type="warning" header="Artifact not found">
          No artifact matched <strong>{artifactId}</strong>. GetArtifactDetail matches by artifactId;
          use artifact search to resolve other identifiers.
        </Alert>
      );
    }
    return (
      <SpaceBetween size="l">
        {otherArtifactIds.length > 0 && (
          <Alert type="info" header={`${otherArtifactIds.length} other artifact(s) matched this identifier`}>
            <SpaceBetween direction="horizontal" size="xs">
              {otherArtifactIds.map(otherId => {
                const href = `/artifact?id=${encodeURIComponent(otherId)}`;
                return (
                  <Link key={otherId} href={href} onFollow={e => { e.preventDefault(); navigate(href); }}>
                    {otherId}
                  </Link>
                );
              })}
            </SpaceBetween>
          </Alert>
        )}

        <ArtifactSummary />
        <PaymentAnalytics />
        <PaymentDetailsTable />
      </SpaceBetween>
    );
  };

  return (
    <Box padding={{ horizontal: 'l', top: 'l', bottom: 'xl' }}>
      <SpaceBetween size="l">
        <SearchTabs />
        {renderBody()}
      </SpaceBetween>
    </Box>
  );
};
